import type { HydratorConfig, HydrationOption } from "./types/index.js";

function setTextInputValue(selector: string, value: string) {
  const widget = $(selector).data("kendoTextBox") as
    | kendo.ui.TextBox
    | undefined;

  if (widget) {
    widget.value(value);
    return;
  }

  const element = document.querySelector(selector) as HTMLInputElement | null;
  if (element) {
    element.value = value;
  }
}

function setNumericInputValue(selector: string, value: number) {
  const widget = $(selector).data("kendoNumericTextBox") as
    | kendo.ui.NumericTextBox
    | undefined;

  if (widget) {
    widget.value(value);
    return;
  }

  const element = document.querySelector(selector) as HTMLInputElement | null;
  if (element) {
    element.value = `${value}`;
  }
}

function setMultiSelectData(selector: string, data: HydrationOption[]) {
  const widget = $(selector).data("kendoMultiSelect") as
    | kendo.ui.MultiSelect
    | undefined;

  if (!widget) {
    return;
  }

  widget.setDataSource(
    new kendo.data.DataSource({
      data,
    }),
  );
}

function setDropdownData(selector: string, data: HydrationOption[]) {
  const widget = $(selector).data("kendoDropDownList") as
    | kendo.ui.DropDownList
    | undefined;

  if (!widget) {
    return;
  }

  widget.setDataSource(
    new kendo.data.DataSource({
      data,
    }),
  );
}

function setMultiSelectValue(selector: string, values: string[]) {
  const widget = $(selector).data("kendoMultiSelect") as
    | kendo.ui.MultiSelect
    | undefined;

  if (!widget) {
    const element = document.querySelector(
      selector,
    ) as HTMLSelectElement | null;
    if (element) {
      Array.from(element.options).forEach((option) => {
        option.selected = values.includes(option.value);
      });
    }

    return;
  }

  if (widget.dataSource.view().length === 0) {
    widget.dataSource.fetch(() => {
      widget.value(values);
    });
    return;
  }

  widget.value(values);
}

function setDropdownValue(selector: string, value: string) {
  const widget = $(selector).data("kendoDropDownList") as
    | kendo.ui.DropDownList
    | undefined;

  if (!widget) {
    const element = document.querySelector(
      selector,
    ) as HTMLSelectElement | null;
    if (element) {
      Array.from(element.options).forEach((option) => {
        option.selected = value === option.value;
      });
    }

    return;
  }

  if (widget.dataSource.view().length === 0) {
    widget.dataSource.fetch(() => {
      widget.value(value);
    });
    return;
  }

  widget.value(value);
}

function setBooleanInputValue(selector: string, value: boolean) {
  let widget:
    | kendo.ui.Switch
    | kendo.ui.CheckBox
    | kendo.ui.RadioButton
    | undefined = $(selector).data("kendoSwitch") as
    | kendo.ui.Switch
    | undefined;

  if (!widget)
    widget = $(selector).data("kendoCheckBox") as kendo.ui.CheckBox | undefined;

  if (!widget)
    widget = $(selector).data("kendoRadioButton") as
      | kendo.ui.RadioButton
      | undefined;

  if (widget) {
    widget.check(value);
    return;
  }

  const element = document.querySelector(selector) as HTMLInputElement | null;
  if (element) {
    element.checked = value;
  }
}

function toStringValue(value: unknown, fallback = "") {
  if (typeof value === "string") {
    return value;
  }

  if (value == null) {
    return fallback;
  }

  return `${value}`;
}

function toNumberValue(value: unknown, fallback = 0) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toBooleanValue(value: unknown, fallback = false) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    if (["true", "1", "yes", "on"].includes(normalized)) {
      return true;
    }

    if (["false", "0", "no", "off", ""].includes(normalized)) {
      return false;
    }
  }

  if (value == null) {
    return fallback;
  }

  return Boolean(value);
}

function toStringArray(value: unknown, fallback: string[] = []) {
  if (!Array.isArray(value)) {
    return fallback;
  }

  return value.map((item) => `${item}`);
}

export function hydrateKendoForm<TResponse extends Record<string, unknown>>(
  response: TResponse,
  config: HydratorConfig<TResponse>,
) {
  for (const field of config.fields) {
    const rawValue = response[field.property];
    const value = field.transform
      ? field.transform(rawValue, response)
      : rawValue;

    switch (field.type) {
      case "text": {
        setTextInputValue(
          field.selector,
          toStringValue(
            value,
            typeof field.fallback === "string" ? field.fallback : "",
          ),
        );
        break;
      }

      case "boolean": {
        setBooleanInputValue(
          field.selector,
          toBooleanValue(
            value,
            typeof field.fallback === "boolean" ? field.fallback : false,
          ),
        );
        break;
      }
      case "numeric": {
        setNumericInputValue(
          field.selector,
          toNumberValue(
            value,
            typeof field.fallback === "number" ? field.fallback : 0,
          ),
        );
        break;
      }

      case "hidden": {
        const element = document.querySelector(
          field.selector,
        ) as HTMLInputElement | null;
        if (element) {
          element.value = toStringValue(
            value,
            typeof field.fallback === "string" ? field.fallback : "",
          );
        }
        break;
      }

      case "content": {
        const element = document.querySelector(field.selector);
        if (element) {
          element.textContent = toStringValue(
            value,
            typeof field.fallback === "string" ? field.fallback : "",
          );
        }
        break;
      }

      case "multiselect": {
        const values = toStringArray(
          value,
          Array.isArray(field.fallback) ? field.fallback : [],
        );

        if (field.getOptions) {
          setMultiSelectData(
            field.selector,
            field.getOptions(rawValue, response),
          );
        }

        setMultiSelectValue(field.selector, values);
        break;
      }
      case "dropdown": {
        const stringValue = toStringValue(
          value,
          typeof field.fallback === "string" ? field.fallback : "",
        );
        if (field.getOptions) {
          setDropdownData(field.selector, field.getOptions(rawValue, response));
        }

        setDropdownValue(field.selector, stringValue);
        break;
      }
    }
  }

  config.afterHydrate?.(response);
}
